import { LocalizedText } from "@/i18n/LocalizedText";
import * as React from "react";
import { PlanSwitcher } from "@/components/PlanSwitcher";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  ChevronsUpDown,
  House,
  SquareDashedBottomCode,
  AtSign,
  Target,
  LayoutGrid,
  ListChecks,
  CircleDollarSign,
  HandHelping,
  Settings,
  TriangleAlert,
  ChevronDown,
  ChevronUp,
  Plus,
  Search,
  ListTodo,
  MoreHorizontal,
  Share2,
  Pencil,
  Pin,
  PinOff,
  Trash2,
} from "lucide-react";
import { auth } from "../firebase";
import { signOut } from "firebase/auth";
import { Link, useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth, type AuthContextType } from "./AuthContext";
import {
  authAPI,
  billingAPI,
  dataChatAPI,
  type DataChatThread,
} from "../services/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import { useLocation } from "react-router-dom";
import { capturePosthogEvent } from "@/lib/posthog";
import { OnboardingGuideCard } from "./OnboardingGuideCard";
import LockOpenIcon from "../assets/lock-opened-icon.svg";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocale } from "@/i18n/LocaleProvider";
import { isWorkspaceMemberTier } from "@/lib/memberTier";
import { useState } from "react";

type AppData = {
  brands: string[];
  navMain: {
    title: string;
    url: string;
    items: {
      title: string;
      url: string;
      href?: string;
      icon: React.ReactNode;
      isActive?: boolean;
      soon?: boolean;
      innerItems?: { title: string; url: string; initial: string }[];
    }[];
  }[];
};

export function withIconSize(icon: any, size: number) {
  return React.cloneElement(icon, { size } as any);
}

const CHAT_GROUP_LABELS = [
  "Today",
  "Last 7 Days",
  "Last 30 Days",
  "Older",
] as const;

type ChatGroupLabel = (typeof CHAT_GROUP_LABELS)[number];

function getBrandSearch(brandId?: string | null) {
  return brandId ? `?brandId=${brandId}` : "";
}

function getDataChatHref(brandId?: string | null, threadId?: string | null) {
  const params = new URLSearchParams();
  if (brandId) params.set("brandId", brandId);
  if (threadId) params.set("threadId", threadId);
  const search = params.toString();
  return `/dashboard/data-chat${search ? `?${search}` : ""}`;
}

function getSidebarItemHref(
  item: { url: string; href?: string },
  brandSearch: string,
) {
  return `/dashboard/${item.href || item.url}${brandSearch}`;
}

function formatThreadAge(value?: string | null) {
  if (!value) return "";
  const date = new Date(value);
  const diffMs = Date.now() - date.getTime();
  if (!Number.isFinite(diffMs) || diffMs < 0) return "";
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diffMs < hour) return `${Math.max(1, Math.floor(diffMs / minute))}m`;
  if (diffMs < day) return `${Math.floor(diffMs / hour)}h`;
  return `${Math.floor(diffMs / day)}d`;
}

function getThreadGroup(value?: string | null): ChatGroupLabel {
  if (!value) return "Older";
  const date = new Date(value);
  const diffMs = Date.now() - date.getTime();
  const day = 24 * 60 * 60 * 1000;
  if (!Number.isFinite(diffMs) || diffMs < 0) return "Today";
  if (diffMs < day) return "Today";
  if (diffMs < 7 * day) return "Last 7 Days";
  if (diffMs < 30 * day) return "Last 30 Days";
  return "Older";
}

export const data: AppData = {
  brands: ["Grammarly.com", "Webflow.com"],
  navMain: [
    {
      title: "Track",
      url: "#",
      items: [
        {
          title: "Dashboard",
          url: "overview",
          icon: <House />,
          isActive: true,
        },
        {
          title: "Prompts",
          url: "prompts",
          icon: <SquareDashedBottomCode />,
        },
        {
          title: "Competitors",
          url: "competitors",
          icon: <LayoutGrid className="rotate-45" />,
        },
        {
          title: "Sources",
          url: "citations",
          href: "citations/cited-pages",
          icon: <AtSign />,
        },
      ],
    },
    {
      title: "Act",
      url: "#",
      items: [
        {
          title: "Foundation",
          url: "get-cited/foundation",
          icon: <Target />,
        },
        {
          title: "Cluster Actions",
          url: "get-cited/cluster-actions",
          icon: <ListChecks />,
          innerItems: [
            {
              title: "Strategy",
              initial: "Strategy",
              url: "get-cited/strategy",
            },
            {
              title: "Tasks",
              initial: "Cluster Actions",
              url: "get-cited/cluster-actions",
            },
          ],
        },
      ],
    },
    {
      title: "Workspace",
      url: "#",
      items: [
        {
          title: "Plans & Pricing",
          url: "plans-pricing",
          icon: <CircleDollarSign />,
        },
        {
          title: "Settings",
          url: "settings",
          icon: <Settings />,
        },
        {
          title: "Support",
          url: "support",
          icon: <HandHelping />,
        },
      ],
    },
  ],
};

const sidebarGroupLabelKeys: Record<string, string> = {
  Track: "shell.group.track",
  Act: "shell.group.act",
  Workspace: "shell.group.workspace",
};

const sidebarItemLabelKeys: Record<string, string> = {
  Dashboard: "shell.nav.dashboard",
  Prompts: "shell.nav.prompts",
  Competitors: "shell.nav.competitors",
  Sources: "shell.nav.sources",
  Foundation: "shell.nav.foundation",
  "Cluster Actions": "shell.nav.clusterActions",
  Strategy: "shell.nav.strategy",
  Tasks: "shell.nav.tasks",
  "Plans & Pricing": "shell.nav.plansPricing",
  Settings: "shell.nav.settings",
  Support: "shell.nav.support",
};

export type Brand = {
  brandId: string;
  id: string;
  country: string;
  name: string;
  url: string;
  currentFocus: string;
  activeProviders?: string[] | null;
  companyId?: string | null;
  company?: {
    id?: string;
    companyId?: string;
    name?: string;
    country?: string | null;
  } | null;
  created_at?: string | Date | null;
  createdAt?: string | Date | null;
  createdDuringSession?: boolean;
  accessRole?: "company_admin" | "company_member" | "brand_member";
};

type AppSidebarProps = React.ComponentProps<typeof Sidebar> & {
  brands: Brand[];
  setBrands: (brands: Brand[]) => void;
  currentBrand: Brand;
  company: any;
  companies?: any[];
  activeItem: string;
  setCurrentBrand: (brand: Brand) => void;
  setCurrentCompany?: (company: any) => void;
  isTrialEnded: boolean;
  user: AuthContextType["user"] | null;
};

export function AppSidebar({
  user,
  isTrialEnded,
  setBrands,
  company,
  companies = [],
  brands,
  currentBrand,
  setCurrentBrand,
  setCurrentCompany = () => {},
  activeItem,
  ...props
}: AppSidebarProps) {
  const { t } = useLocale();
  const { setLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { state, isMobile, setOpenMobile } = useSidebar();
  const trialBannerTrackedRef = React.useRef<string>("");
  const activeAccessRole = currentBrand?.accessRole || company?.role;
  const isCollaborator = activeAccessRole
    ? activeAccessRole !== "company_admin"
    : isWorkspaceMemberTier(user?.tier);
  const { data: billingSubscription } = useQuery({
    queryKey: ["billing-subscription", user?.id ?? "me"],
    queryFn: billingAPI.getSubscription,
    enabled: !isCollaborator,
    staleTime: 60_000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
  const hasBillingStatus = Boolean(billingSubscription?.subscription_status);
  const isStripeTrialing =
    billingSubscription?.subscription_status === "trialing";
  const showLegacyTrial = !hasBillingStatus && user?.tier === "trial";
  const [openedAction, setOpenedAction] = useState(true);
  const [chatSearch, setChatSearch] = useState("");
  const [renamingThreadId, setRenamingThreadId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [openChatMenuThreadId, setOpenChatMenuThreadId] = useState<
    string | null
  >(null);
  const [threadPendingDelete, setThreadPendingDelete] =
    useState<DataChatThread | null>(null);
  const queryClient = useQueryClient();
  const trialEndsAt = billingSubscription?.trial_end || user?.expired_at;
  const showTrialState = isStripeTrialing || showLegacyTrial;
  const brandSearchValue = currentBrand?.brandId || currentBrand?.id;
  const brandSearch = getBrandSearch(brandSearchValue) || location.search || "";
  const isDataChatRoute = location.pathname === "/dashboard/data-chat";
  const activeThreadId = React.useMemo(
    () => new URLSearchParams(location.search).get("threadId"),
    [location.search],
  );
  const { data: chatThreadResponse, isLoading: chatThreadsLoading } = useQuery({
    queryKey: ["data-chat-threads", brandSearchValue],
    queryFn: async () => {
      if (!brandSearchValue) return { ok: true, threads: [] };
      return dataChatAPI.getThreads(brandSearchValue);
    },
    enabled:
      Boolean(brandSearchValue) && isDataChatRoute && state !== "collapsed",
    staleTime: 30_000,
  });
  const chatThreads = React.useMemo(
    () => chatThreadResponse?.threads || [],
    [chatThreadResponse?.threads],
  );
  const filteredChatThreads = React.useMemo(() => {
    const normalizedSearch = chatSearch.trim().toLowerCase();
    return chatThreads.filter((thread) =>
      normalizedSearch
        ? thread.title.toLowerCase().includes(normalizedSearch)
        : true,
    );
  }, [chatSearch, chatThreads]);
  const pinnedChatThreads = React.useMemo(() => {
    return filteredChatThreads
      .filter((thread) => thread.pinnedAt)
      .sort(
        (a, b) =>
          new Date(b.pinnedAt || 0).getTime() -
          new Date(a.pinnedAt || 0).getTime(),
      );
  }, [filteredChatThreads]);
  const chatThreadGroups = React.useMemo(() => {
    const groups: Record<ChatGroupLabel, DataChatThread[]> = {
      Today: [],
      "Last 7 Days": [],
      "Last 30 Days": [],
      Older: [],
    };
    filteredChatThreads
      .filter((thread) => !thread.pinnedAt)
      .forEach((thread) => {
        groups[getThreadGroup(thread.updatedAt)].push(thread);
      });
    return groups;
  }, [filteredChatThreads]);
  const getChatGroupDisplayLabel = React.useCallback(
    (label: ChatGroupLabel) => {
      if (label === "Today") return t("dataChat.sidebar.today");
      if (label === "Last 7 Days") return t("dataChat.sidebar.last7Days");
      if (label === "Last 30 Days") return t("dataChat.sidebar.last30Days");
      return t("dataChat.sidebar.older");
    },
    [t],
  );
  const showTrialBanner = (showTrialState || isTrialEnded) && !isCollaborator;
  const isTrialExpanded =
    (showTrialState || isTrialEnded) &&
    !isCollaborator &&
    state !== "collapsed";
  const isTrialCollapsed =
    (showTrialState || isTrialEnded) &&
    !isCollaborator &&
    state === "collapsed";
  function timeUntil(target?: string): string {
    if (!target) return "0 days";
    const now = new Date();
    const targetDate = new Date(target);
    const msInDay = 1000 * 60 * 60 * 24;
    const diff = targetDate.getTime() - now.getTime();
    const days =
      !Number.isFinite(diff) || diff <= 0 ? 0 : Math.ceil(diff / msInDay);
    return `${days} day${days === 1 ? "" : "s"}`;
  }

  function daysLeft(target?: string): number {
    if (!target) return 0;
    const now = new Date();
    const targetDate = new Date(target);
    const msInDay = 1000 * 60 * 60 * 24;
    const diff = targetDate.getTime() - now.getTime();
    if (!Number.isFinite(diff) || diff <= 0) return 0;
    return Math.max(0, Math.ceil(diff / msInDay));
  }

  const getSidebarNavTargetId = (url: string): string => {
    return `sidebar-nav-${String(url || "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")}`;
  };

  const handleNewChat = () => {
    capturePosthogEvent("agent_new_chat_started", {
      brand_id: brandSearchValue || null,
    });
    navigate(getDataChatHref(brandSearchValue));
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const deleteThreadMutation = useMutation({
    mutationFn: async (thread: DataChatThread) => {
      if (!brandSearchValue) throw new Error("Missing brand");
      return dataChatAPI.deleteThread(brandSearchValue, thread.id);
    },
    onSuccess: (_response, thread) => {
      setThreadPendingDelete(null);
      queryClient.invalidateQueries({
        queryKey: ["data-chat-threads", brandSearchValue],
      });
      if (activeThreadId === thread.id) {
        navigate(getDataChatHref(brandSearchValue));
      }
    },
    onError: () => {
      toast.error(t("legacy.couldnt.delete.chat.5kgppq"));
    },
  });

  const updateThreadTitleMutation = useMutation({
    mutationFn: async ({
      threadId,
      title,
    }: {
      threadId: string;
      title: string;
    }) => {
      if (!brandSearchValue) throw new Error("Missing brand");
      return dataChatAPI.updateThreadTitle(brandSearchValue, threadId, title);
    },
    onMutate: async ({ threadId, title }) => {
      const queryKey = ["data-chat-threads", brandSearchValue] as const;
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<{
        ok: boolean;
        threads: DataChatThread[];
      }>(queryKey);
      queryClient.setQueryData<{ ok: boolean; threads: DataChatThread[] }>(
        queryKey,
        (current) =>
          current
            ? {
                ...current,
                threads: current.threads.map((thread) =>
                  thread.id === threadId ? { ...thread, title } : thread,
                ),
              }
            : current,
      );
      queryClient.setQueryData<{
        ok: boolean;
        thread: DataChatThread;
        messages?: unknown[];
      }>(["data-chat-thread", brandSearchValue, threadId], (current) =>
        current?.thread
          ? { ...current, thread: { ...current.thread, title } }
          : current,
      );
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          ["data-chat-threads", brandSearchValue],
          context.previous,
        );
      }
      toast.error(t("legacy.couldnt.rename.chat.10ixzon"));
    },
    onSuccess: (response) => {
      queryClient.setQueryData<{ ok: boolean; threads: DataChatThread[] }>(
        ["data-chat-threads", brandSearchValue],
        (current) =>
          current
            ? {
                ...current,
                threads: current.threads.map((thread) =>
                  thread.id === response.thread.id ? response.thread : thread,
                ),
              }
            : current,
      );
      queryClient.setQueryData<{
        ok: boolean;
        thread: DataChatThread;
        messages?: unknown[];
      }>(
        ["data-chat-thread", brandSearchValue, response.thread.id],
        (current) =>
          current?.thread ? { ...current, thread: response.thread } : current,
      );
    },
    onSettled: (_response, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["data-chat-threads", brandSearchValue],
      });
      if (variables?.threadId) {
        queryClient.invalidateQueries({
          queryKey: ["data-chat-thread", brandSearchValue, variables.threadId],
        });
      }
    },
  });

  const updateThreadPinMutation = useMutation({
    mutationFn: async ({
      threadId,
      pinned,
    }: {
      threadId: string;
      pinned: boolean;
    }) => {
      if (!brandSearchValue) throw new Error("Missing brand");
      return dataChatAPI.updateThreadPin(brandSearchValue, threadId, pinned);
    },
    onMutate: async ({ threadId, pinned }) => {
      const queryKey = ["data-chat-threads", brandSearchValue] as const;
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<{
        ok: boolean;
        threads: DataChatThread[];
      }>(queryKey);
      const pinnedAt = pinned ? new Date().toISOString() : null;
      queryClient.setQueryData<{ ok: boolean; threads: DataChatThread[] }>(
        queryKey,
        (current) =>
          current
            ? {
                ...current,
                threads: current.threads.map((thread) =>
                  thread.id === threadId ? { ...thread, pinnedAt } : thread,
                ),
              }
            : current,
      );
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          ["data-chat-threads", brandSearchValue],
          context.previous,
        );
      }
      toast.error(t("legacy.couldnt.update.pinned.chat.1w0ahum"));
    },
    onSuccess: (response) => {
      queryClient.setQueryData<{ ok: boolean; threads: DataChatThread[] }>(
        ["data-chat-threads", brandSearchValue],
        (current) =>
          current
            ? {
                ...current,
                threads: current.threads.map((thread) =>
                  thread.id === response.thread.id ? response.thread : thread,
                ),
              }
            : current,
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["data-chat-threads", brandSearchValue],
      });
    },
  });

  const togglePinnedChat = (thread: DataChatThread) => {
    updateThreadPinMutation.mutate({
      threadId: thread.id,
      pinned: !thread.pinnedAt,
    });
  };

  const beginRenameChat = (thread: DataChatThread) => {
    setRenamingThreadId(thread.id);
    setRenameDraft(thread.title);
  };

  const commitRenameChat = () => {
    if (!renamingThreadId) return;
    const nextTitle = renameDraft.trim();
    const currentThread = chatThreads.find(
      (thread) => thread.id === renamingThreadId,
    );
    if (nextTitle && nextTitle !== currentThread?.title) {
      updateThreadTitleMutation.mutate({
        threadId: renamingThreadId,
        title: nextTitle,
      });
    }
    setRenamingThreadId(null);
    setRenameDraft("");
  };

  const renderChatThreadItem = (thread: DataChatThread) => {
    const isActive = activeThreadId === thread.id;
    const isPinned = Boolean(thread.pinnedAt);
    const isMenuOpen = openChatMenuThreadId === thread.id;
    const href = getDataChatHref(brandSearchValue, thread.id);

    return (
      <div
        key={thread.id}
        className={`group relative flex h-7 items-center rounded-md text-[13px] transition-colors hover:bg-white/[0.08] hover:text-white ${
          isActive ? "bg-white/10 text-white" : "text-white/75"
        }`}
      >
        {renamingThreadId === thread.id ? (
          <input
            value={renameDraft}
            onChange={(event) => setRenameDraft(event.currentTarget.value)}
            onBlur={commitRenameChat}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                event.currentTarget.blur();
              }
              if (event.key === "Escape") {
                setRenamingThreadId(null);
                setRenameDraft("");
              }
            }}
            autoFocus
            className="h-full min-w-0 flex-1 rounded-md border border-white/20 bg-black/20 px-2 text-[13px] text-white outline-none"
          />
        ) : (
          <Link
            to={href}
            onClick={() => {
              capturePosthogEvent("agent_chat_opened", {
                brand_id: brandSearchValue || null,
                chat_id: thread.id,
              });
              if (isMobile) {
                setOpenMobile(false);
              }
            }}
            className="flex min-w-0 flex-1 items-center gap-1.5 px-2 pr-10"
          >
            {isPinned ? (
              <Pin className="h-3.5 w-3.5 shrink-0 text-white/45" />
            ) : null}
            <span className="min-w-0 truncate">{thread.title}</span>
          </Link>
        )}
        {renamingThreadId !== thread.id ? (
          <div className="absolute inset-y-0 right-1 flex w-8 items-center justify-center">
            <span
              className={`pointer-events-none absolute inset-0 flex items-center justify-center text-center text-[11px] text-white/45 transition-opacity ${
                isMenuOpen ? "opacity-0" : "group-hover:opacity-0"
              }`}
            >
              {formatThreadAge(thread.updatedAt)}
            </span>
            <DropdownMenu
              open={isMenuOpen}
              onOpenChange={(open) =>
                setOpenChatMenuThreadId(open ? thread.id : null)
              }
            >
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label={t("dataChat.sidebar.chatOptions")}
                  className={`flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded-md text-white/65 transition-opacity hover:bg-white/10 hover:text-white ${
                    isMenuOpen
                      ? "pointer-events-auto opacity-100"
                      : "pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-100"
                  }`}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                side="bottom"
                sideOffset={6}
                collisionPadding={8}
                avoidCollisions={false}
                className="w-56 rounded-xl border-white/10 bg-[#303030] p-2 text-white"
              >
                <DropdownMenuItem
                  disabled
                  className="gap-3 text-white/60 focus:bg-white/10 focus:text-white/60"
                >
                  <Share2 className="h-4 w-4" />
                  {t("dataChat.action.share")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="gap-3 focus:bg-white/10 focus:text-white"
                  onClick={() => beginRenameChat(thread)}
                >
                  <Pencil className="h-4 w-4" />
                  {t("dataChat.sidebar.rename")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="gap-3 focus:bg-white/10 focus:text-white"
                  disabled={updateThreadPinMutation.isPending}
                  onClick={() => togglePinnedChat(thread)}
                >
                  {isPinned ? (
                    <PinOff className="h-4 w-4" />
                  ) : (
                    <Pin className="h-4 w-4" />
                  )}
                  {isPinned
                    ? t("dataChat.sidebar.unpin")
                    : t("dataChat.sidebar.pin")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="gap-3 text-[#F87171] focus:bg-[#3A1D1D] focus:text-[#F87171]"
                  onClick={() => setThreadPendingDelete(thread)}
                >
                  <Trash2 className="h-4 w-4" />
                  {t("dataChat.action.delete")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ) : null}
      </div>
    );
  };

  React.useEffect(() => {
    if (!showTrialBanner) return;
    const trackingKey = `${user?.id || "unknown"}:${brandSearchValue || "none"}`;
    if (trialBannerTrackedRef.current === trackingKey) return;
    trialBannerTrackedRef.current = trackingKey;
    capturePosthogEvent("trial_banner_viewed", {
      days_left: trialEndsAt ? daysLeft(trialEndsAt) : 0,
    });
  }, [brandSearchValue, showTrialBanner, trialEndsAt, user?.id]);

  const handleTrialUpgradeClick = () => {
    capturePosthogEvent("trial_upgrade_clicked", {
      source: "sidebar_banner",
    });
    navigate(`/dashboard/plans-pricing${brandSearch}`);
  };

  const handleLogout = async () => {
    setLoading(true);
    try {
      const [apiLogoutResult, firebaseSignOutResult] = await Promise.allSettled(
        [authAPI.logout(), signOut(auth)],
      );

      if (
        apiLogoutResult.status === "rejected" &&
        firebaseSignOutResult.status === "rejected"
      ) {
        toast.error(t("legacy.couldnt.log.out.1diajr7"), {
          icon: (
            <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
              <TriangleAlert className="w-4 h-4 text-black" />
            </div>
          ),
          position: "bottom-right",
        });
      }

      navigate("/login", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  return (
    <aside className="dark ">
      <Sidebar {...props} collapsible="icon">
        <SidebarHeader>
          <PlanSwitcher
            setBrands={setBrands}
            isTrialEnded={isTrialEnded}
            brands={brands}
            defaultBrand={currentBrand}
            company={company}
            companies={companies}
            setCurrentBrand={setCurrentBrand}
            setCurrentCompany={setCurrentCompany}
          />
        </SidebarHeader>
        {isTrialExpanded ? (
          <div
            className={
              "rounded-xl mx-2 mt-2 mb-2 bg-[linear-gradient(360deg,#220400_-77.91%,#E33501_100%)] text-white py-2 px-3 relative overflow-hidden flex flex-col gap-2"
            }
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <img src={LockOpenIcon}></img>
                <div className="text-sm">
                  {showTrialState && !isTrialEnded
                    ? `Trial · ${timeUntil(trialEndsAt)} left`
                    : "Your plan has ended"}
                </div>
              </div>
              <Button
                variant="secondary"
                size="sm"
                className="py-0 h-[24px] hover:bg-[#ff8138] hover:text-white text-white cursor-pointer bg-[#FF5E00]"
                onClick={handleTrialUpgradeClick}
              >
                {showTrialState && !isTrialEnded ? "Upgrade" : "Upgrade"}
              </Button>
            </div>
          </div>
        ) : isTrialCollapsed ? (
          <div className="rounded-xl mx-3 mt-2 bg-[linear-gradient(360deg,#220400_-77.91%,#E33501_100%)] text-white py-2 px-1 relative overflow-hidden flex flex-col gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <img className="h-4 cursor-pointer" src={LockOpenIcon}></img>
              </TooltipTrigger>
              <TooltipContent side="right">
                {showTrialState && !isTrialEnded
                  ? `Trial · ${timeUntil(trialEndsAt)} left`
                  : "Your plan has ended"}
              </TooltipContent>
            </Tooltip>
          </div>
        ) : null}
        {state !== "collapsed" ? (
          <div className="mx-2 mb-2 grid h-8 grid-cols-2 rounded-lg bg-white/5 p-0.5 text-sm">
            <button
              type="button"
              onClick={() => navigate(`/dashboard/overview${brandSearch}`)}
              className={`cursor-pointer rounded-md px-3 text-center transition-colors ${
                isDataChatRoute
                  ? "text-white/55 hover:text-white"
                  : "bg-white/10 text-white shadow-sm"
              }`}
            >
              {t("dataChat.sidebar.browse")}
            </button>
            <button
              type="button"
              onClick={() => navigate(getDataChatHref(brandSearchValue))}
              className={`flex cursor-pointer items-center justify-center gap-2 rounded-md px-3 transition-colors ${
                isDataChatRoute
                  ? "bg-[#0A0A0A] text-white shadow-sm"
                  : "text-white/80 hover:text-white"
              }`}
            >
              {t("dataChat.sidebar.chat")}
              <span className="rounded-[5px] border border-[#FF5E00]/60 px-1 text-[10px] font-medium leading-4 text-[#FF5E00]">
                {t("dataChat.sidebar.newBadge")}
              </span>
            </button>
          </div>
        ) : null}

        <SidebarContent>
          {isDataChatRoute && state !== "collapsed" ? (
            <div className="flex min-h-0 flex-1 flex-col px-2 pb-2 text-sm text-white/80">
              <button
                type="button"
                onClick={handleNewChat}
                className={`flex h-8 cursor-pointer items-center gap-2 rounded-md px-2 text-left transition-colors hover:bg-white/[0.08] hover:text-white ${
                  !activeThreadId ? "text-white" : "text-white/85"
                }`}
              >
                <Plus className="h-4 w-4" />
                <span>{t("dataChat.thread.new")}</span>
              </button>
              <button
                type="button"
                disabled
                className="mt-1 flex h-8 cursor-not-allowed items-center gap-2 rounded-md px-2 text-left text-white/30"
              >
                <ListTodo className="h-4 w-4" />
                <span>{t("dataChat.sidebar.tasks")}</span>
              </button>
              <div className="relative mt-4">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-white/55" />
                <input
                  value={chatSearch}
                  onChange={(event) => {
                    const value = event.currentTarget.value;
                    setChatSearch(value);
                    if (value.trim()) {
                      capturePosthogEvent("agent_chat_searched", {
                        brand_id: brandSearchValue || null,
                      });
                    }
                  }}
                  placeholder={t("dataChat.sidebar.searchChats")}
                  className="h-8 w-full rounded-lg border border-white/15 bg-transparent pl-8 pr-2 text-sm text-white outline-none placeholder:text-white/45 focus:border-white/30"
                />
              </div>
              <div className="mt-3 min-h-0 flex-1 overflow-y-auto pr-1">
                {pinnedChatThreads.length ? (
                  <div className="mb-3">
                    <div className="mb-1 px-0.5 text-[11px] font-medium text-white/45">
                      {t("dataChat.sidebar.pinned")}
                    </div>
                    <div className="space-y-0.5">
                      {pinnedChatThreads.map(renderChatThreadItem)}
                    </div>
                  </div>
                ) : null}
                {CHAT_GROUP_LABELS.map((label) => {
                  const threads = chatThreadGroups[label];
                  if (!threads.length) return null;
                  return (
                    <div key={label} className="mb-3">
                      <div className="mb-1 px-0.5 text-[11px] font-medium text-white/45">
                        {getChatGroupDisplayLabel(label)}
                      </div>
                      <div className="space-y-0.5">
                        {threads.map(renderChatThreadItem)}
                      </div>
                    </div>
                  );
                })}
                {chatThreadsLoading ? (
                  <div className="px-2 py-2 text-xs text-white/45">
                    {t("dataChat.sidebar.loadingChats")}
                  </div>
                ) : null}
                {!chatThreadsLoading &&
                !pinnedChatThreads.length &&
                !CHAT_GROUP_LABELS.some(
                  (label) => chatThreadGroups[label].length,
                ) ? (
                  <div className="px-2 py-2 text-xs leading-5 text-white/45">
                    {t("dataChat.sidebar.noChats")}
                  </div>
                ) : null}
              </div>
            </div>
          ) : (
            data.navMain.map((item) => (
              <SidebarGroup key={item.title}>
                <SidebarGroupLabel>
                  {t(sidebarGroupLabelKeys[item.title] || item.title)}
                </SidebarGroupLabel>
                <SidebarGroupContent
                  className={`${state === "collapsed" && "w-[47px]"}`}
                >
                  <SidebarMenu className="flex justify-center">
                    <TooltipProvider>
                      {item.items.map((item) => {
                        if (
                          isCollaborator &&
                          item.title === "Plans & Pricing"
                        ) {
                          return null;
                        }
                        if (item.soon) return null;

                        const menuButton = (
                          <div
                            className={`flex flex-col ${state === "expanded" && "w-full"}`}
                          >
                            <SidebarMenuButton
                              className="justify-between"
                              asChild
                              isActive={activeItem === item.title}
                            >
                              <div
                                className={`${item.title === "Cluster Actions" && "cursor-pointer"}`}
                                onClick={() => {
                                  if (
                                    item.title === "Cluster Actions" &&
                                    state === "expanded"
                                  ) {
                                    setOpenedAction(!openedAction);
                                  }
                                }}
                              >
                                <Link
                                  to={getSidebarItemHref(item, brandSearch)}
                                  data-onboarding-target={getSidebarNavTargetId(
                                    item.url,
                                  )}
                                  className="w-full"
                                >
                                  <p className="flex items-center gap-2 w-full">
                                    {withIconSize(
                                      item.icon,
                                      state === "collapsed" ? 20 : 16,
                                    )}
                                    {state !== "collapsed" && (
                                      <span>
                                        {t(
                                          sidebarItemLabelKeys[item.title] ||
                                            item.title,
                                        )}
                                      </span>
                                    )}
                                  </p>
                                </Link>
                                {item?.innerItems?.length &&
                                  (openedAction ? (
                                    <ChevronDown></ChevronDown>
                                  ) : (
                                    <ChevronUp></ChevronUp>
                                  ))}
                              </div>
                            </SidebarMenuButton>
                            {item?.innerItems?.length &&
                              state === "expanded" && (
                                <div
                                  className={`
                              overflow-hidden transition-all duration-300 ease-in-out
                              flex flex-col ${state === "expanded" && "border-l"} border-[#868785] pl-3 mt-3 ml-3 mr-4
                              ${openedAction ? "max-h-[300px] opacity-100" : "max-h-0 opacity-0 p-0"}`}
                                >
                                  {item.innerItems.map(
                                    (i: { title: string; url: string }) => {
                                      return (
                                        <SidebarMenuButton
                                          key={i.url}
                                          className="justify-between"
                                          asChild
                                          isActive={activeItem === i.title}
                                        >
                                          <Link
                                            to={`${i.url}${brandSearch}`}
                                            data-onboarding-target={getSidebarNavTargetId(
                                              i.url,
                                            )}
                                          >
                                            <p className="flex items-center gap-2">
                                              <span>
                                                {t(
                                                  sidebarItemLabelKeys[
                                                    i.title
                                                  ] || i.title,
                                                )}
                                              </span>
                                            </p>
                                          </Link>
                                        </SidebarMenuButton>
                                      );
                                    },
                                  )}
                                </div>
                              )}
                          </div>
                        );

                        return (
                          <SidebarMenuItem
                            key={item.title}
                            className="flex justify-center"
                          >
                            {state === "collapsed" ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  {menuButton}
                                </TooltipTrigger>
                                <TooltipContent side="right">
                                  {t(
                                    sidebarItemLabelKeys[item.title] ||
                                      item.title,
                                  )}
                                </TooltipContent>
                              </Tooltip>
                            ) : (
                              menuButton
                            )}
                          </SidebarMenuItem>
                        );
                      })}
                    </TooltipProvider>
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            ))
          )}
        </SidebarContent>
        <SidebarFooter>
          {state !== "collapsed" ? (
            <div className="mt-auto pt-2">
              <OnboardingGuideCard
                brandId={brandSearchValue || null}
                currentPath={`${location.pathname}${location.search || ""}`}
                onNavigate={(to) => {
                  navigate(to);
                  if (isMobile) {
                    setOpenMobile(false);
                  }
                }}
              />
            </div>
          ) : null}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              {state !== "collapsed" && (
                <div className="flex items-center gap-3 p-3 cursor-pointer rounded-md hover:bg-muted transition-colors">
                  <Avatar className="h-8 w-8 rounded">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback>
                      {((user?.name as string) || "User")
                        ?.split(" ")
                        .map((n) => n[0])
                        .join("")
                        .toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="text-left space-y-0.5">
                    <div className="text-sm font-medium text-white">
                      {user?.name || "User"}
                    </div>
                    <div className="text-xs text-muted-foreground overflow-hidden text-ellipsis max-w-[140px]">
                      {user?.email || "Loading..."}
                    </div>
                  </div>
                  <ChevronsUpDown className="ml-auto h-4 w-4 text-muted-foreground" />
                </div>
              )}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem
                onClick={handleLogout}
                className="cursor-pointer"
              >
                <LocalizedText id="legacy.log.out.mgz4hb" />
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={() => {
                  navigate("/dashboard/settings/account");
                }}
              >
                <LocalizedText id="legacy.settings.aquvsw" />
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarFooter>
        <Dialog
          open={Boolean(threadPendingDelete)}
          onOpenChange={(open) => {
            if (!open) setThreadPendingDelete(null);
          }}
        >
          <DialogContent className="max-w-md rounded-2xl border-[#3A3A3A] bg-[#202020] text-white shadow-2xl">
            <DialogHeader>
              <DialogTitle className="text-2xl font-semibold text-white">
                {t("dataChat.sidebar.deleteTitle")}
              </DialogTitle>
              <DialogDescription className="text-base leading-7 text-white/80">
                {t("dataChat.sidebar.deleteDescription")}{" "}
                <span className="font-semibold text-white">
                  {threadPendingDelete?.title || t("dataChat.sidebar.thisChat")}
                </span>
                .
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:justify-end">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setThreadPendingDelete(null)}
                className="rounded-full bg-black px-5 text-white hover:bg-black/80 hover:text-white"
              >
                {t("dataChat.action.cancel")}
              </Button>
              <Button
                type="button"
                onClick={() => {
                  if (threadPendingDelete) {
                    deleteThreadMutation.mutate(threadPendingDelete);
                  }
                }}
                disabled={deleteThreadMutation.isPending}
                className="rounded-full bg-[#DC3D36] px-5 text-white hover:bg-[#C93530] hover:text-white"
              >
                {t("dataChat.action.delete")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <SidebarRail />
      </Sidebar>
    </aside>
  );
}
